import { useCallback, useRef } from 'react';

interface UseInputHistoryReturn {
  /**
   * Recall the previous (older) entry. Pass the current draft so navigation only starts
   * from an empty input, preserving caret movement while the user is composing.
   * Returns the value to display, or `null` to leave the input unchanged.
   */
  recallOlder: (draft: string) => string | null;
  /**
   * Recall the next (newer) entry while browsing; stepping past the most recent entry
   * returns an empty string to restore the draft. Returns `null` when not browsing.
   */
  recallNewer: () => string | null;
  /** Exit history browsing (e.g. after the user types or submits). */
  reset: () => void;
}

/**
 * Shell-style history navigation over a list of prior text entries (ordered oldest →
 * newest), as used by chat inputs and terminals: Up steps back through past entries,
 * Down steps forward and returns to the live draft past the newest.
 *
 * The browsing cursor is kept in a ref so stepping is synchronous and independent of the
 * caller's render cycle. The hook is value-agnostic — it returns the string to show and
 * lets the caller own the input state.
 *
 * @param entries Prior entries, oldest first.
 */
export const useInputHistory = (entries: string[]): UseInputHistoryReturn => {
  // Cursor into `entries` while browsing; `null` means the live draft (not browsing).
  const indexRef = useRef<number | null>(null);

  const reset = useCallback(() => {
    indexRef.current = null;
  }, []);

  const recallOlder = useCallback(
    (draft: string): string | null => {
      if (entries.length === 0) return null;
      if (indexRef.current === null) {
        // Only begin browsing from an empty draft, so Up still moves the caret when the
        // user is mid-composing (including within a multi-line message).
        if (draft) return null;
        indexRef.current = entries.length - 1;
      } else {
        // Already browsing: step to the previous entry, holding at the oldest.
        indexRef.current = Math.max(0, indexRef.current - 1);
      }
      return entries[indexRef.current];
    },
    [entries],
  );

  const recallNewer = useCallback((): string | null => {
    if (indexRef.current === null) return null;
    const next = indexRef.current + 1;
    if (next >= entries.length) {
      // Stepped past the most recent entry: leave history and restore the empty draft.
      indexRef.current = null;
      return '';
    }
    indexRef.current = next;
    return entries[next];
  }, [entries]);

  return { recallOlder, recallNewer, reset };
};
